import { useState } from 'react';
import { ChevronRight, Lock, Search } from 'lucide-react';
import {
  PERMISSION_SECTIONS,
  ROLE_LEVEL,
  screenKeys,
  type PermissionAction,
  type PermissionKey,
  type PermissionScreen,
} from '@supernova/shared';

const COLUMNS = [
  ['view', 'View'],
  ['create', 'Create'],
  ['edit', 'Edit'],
  ['delete', 'Delete'],
] as const;

const LOCK_TEXT = { owner: 'Owner only', admin: 'Owner and Admin only', everyone: 'Everyone has this' };

const matches = (screen: PermissionScreen, q: string) =>
  [
    screen.label,
    screen.hint,
    screen.group ?? '',
    ...[screen.view, screen.create, screen.edit, screen.delete, ...(screen.more ?? [])].map((x) => x?.label ?? ''),
  ]
    .join(' ')
    .toLowerCase()
    .includes(q);

/**
 * Every permission, laid out like the sidebar: a section per tab, a row per
 * screen with View / Create / Edit / Delete and the screen's extra actions.
 * Ticking an action ticks its View; unticking View clears the row.
 * Shared by the custom role editor and the Users page's per-user extras.
 */
export function PermissionGrid({
  isChecked,
  isLocked,
  lockedLabel,
  onChange,
  roleLevel = 0,
  initiallyOpen = [],
}: {
  isChecked: (key: PermissionKey) => boolean;
  /** A locked box can't be changed; `lockedLabel` says why when the role supplies it. */
  isLocked: (key: PermissionKey) => boolean;
  lockedLabel?: (key: PermissionKey) => string | null;
  onChange: (add: PermissionKey[], remove: PermissionKey[]) => void;
  /** The role's level, which decides the Owner and Admin only rows. */
  roleLevel?: number;
  /** Section ids to start open. */
  initiallyOpen?: string[];
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(initiallyOpen));
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  // A screen's View shows ticked, and stays put, while anything else on the row is ticked.
  const rowState = (screen: PermissionScreen) => {
    const keys = screenKeys(screen);
    const view = screen.view?.key;
    const actions = keys.filter((k) => k !== view);
    const viewHeld = actions.some((k) => isChecked(k));
    const viewPinned = actions.some((k) => isChecked(k) && isLocked(k));
    const checked = (k: PermissionKey) => isChecked(k) || (k === view && viewHeld);
    const locked = (k: PermissionKey) => isLocked(k) || (k === view && viewPinned);
    return { keys, view, checked, locked };
  };

  const toggle = (screen: PermissionScreen, key: PermissionKey, on: boolean) => {
    const { keys, view, checked, locked } = rowState(screen);
    if (on) onChange(view && key !== view && !checked(view) ? [key, view] : [key], []);
    else if (key === view)
      onChange(
        [],
        keys.filter((k) => checked(k) && !locked(k)),
      );
    else onChange([], [key]);
  };

  const lockStatus = (who: NonNullable<PermissionScreen['lockedTo']>) => {
    const needed = who === 'owner' ? ROLE_LEVEL.owner : ROLE_LEVEL.admin;
    if (who === 'everyone') return { text: LOCK_TEXT.everyone, on: true };
    if (roleLevel >= needed) return { text: 'Included at this level', on: true };
    return { text: LOCK_TEXT[who], on: false };
  };

  const box = (screen: PermissionScreen, action: PermissionAction, column: string) => {
    const { checked, locked } = rowState(screen);
    const reason = locked(action.key) ? (lockedLabel?.(action.key) ?? null) : null;
    return (
      <label
        title={reason ? `${action.label} (from ${reason})` : action.label}
        className={`flex min-h-11 items-center justify-center ${locked(action.key) ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <input
          type="checkbox"
          checked={checked(action.key)}
          disabled={locked(action.key)}
          onChange={(e) => toggle(screen, action.key, e.target.checked)}
          data-permission={action.key}
          aria-label={`${screen.label}, ${column}: ${action.label}`}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-60"
        />
      </label>
    );
  };

  const pill = (screen: PermissionScreen, action: PermissionAction) => {
    const { checked, locked } = rowState(screen);
    const on = checked(action.key);
    const reason = locked(action.key) ? (lockedLabel?.(action.key) ?? null) : null;
    return (
      <label
        key={action.key}
        title={reason ? `From ${reason}` : undefined}
        className={`inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${
          on ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border bg-white text-muted-foreground'
        } ${locked(action.key) ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <input
          type="checkbox"
          checked={on}
          disabled={locked(action.key)}
          onChange={(e) => toggle(screen, action.key, e.target.checked)}
          data-permission={action.key}
          aria-label={`${screen.label}: ${action.label}`}
          className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-60"
        />
        {action.label}
        {action.movesMoney && (
          <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-800">
            Moves money
          </span>
        )}
      </label>
    );
  };

  const anyMatch = !q || PERMISSION_SECTIONS.some((s) => s.screens.some((sc) => matches(sc, q)));

  return (
    <div className="overflow-hidden rounded-lg border border-input bg-background">
      <div className="flex flex-wrap items-center gap-2 border-b border-border p-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-white px-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a permission, e.g. refund"
            aria-label="Find a permission"
            className="h-9 min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen(new Set(PERMISSION_SECTIONS.map((s) => s.id)))}
          className="h-9 rounded-md border border-border bg-white px-3 text-xs font-medium hover:bg-muted"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={() => setOpen(new Set())}
          className="h-9 rounded-md border border-border bg-white px-3 text-xs font-medium hover:bg-muted"
        >
          Collapse all
        </button>
      </div>
      <p className="border-b border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        Ticking Create, Edit, Delete or an extra action also ticks View. Unticking View clears the row. A dash means the
        screen has no such action.
      </p>
      {!anyMatch && <p className="p-6 text-center text-sm text-muted-foreground">No permission matches “{query}”.</p>}
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          {PERMISSION_SECTIONS.map((section) => {
            const screens = q ? section.screens.filter((sc) => matches(sc, q)) : section.screens;
            if (screens.length === 0) return null;
            const keys = section.screens.flatMap(screenKeys);
            const states = section.screens.map((sc) => ({ sc, ...rowState(sc) }));
            const held = states.reduce((n, s) => n + s.keys.filter((k) => s.checked(k)).length, 0);
            const free = states.flatMap((s) => s.keys.filter((k) => !s.locked(k)).map((k) => ({ k, s })));
            const summary =
              keys.length === 0
                ? 'Set by role'
                : held === 0
                  ? 'No access'
                  : held === keys.length
                    ? 'Full access'
                    : `${held} of ${keys.length}`;
            const isOpen = q ? true : open.has(section.id);
            const setOpenTo = (value: boolean) =>
              setOpen((prev) => {
                const next = new Set(prev);
                if (value) next.add(section.id);
                else next.delete(section.id);
                return next;
              });
            let lastGroup: string | undefined;
            return (
              <div key={section.id} className="border-b border-border last:border-b-0" data-section={section.id}>
                <div className="flex items-center gap-2 px-1.5 py-1">
                  <button
                    type="button"
                    onClick={() => setOpenTo(!isOpen)}
                    aria-expanded={isOpen}
                    className="flex min-h-11 flex-1 items-center gap-2 rounded-md px-2 text-left text-sm font-semibold hover:bg-muted/50"
                  >
                    <ChevronRight
                      className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
                      aria-hidden="true"
                    />
                    {section.label}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        held > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {summary}
                    </span>
                  </button>
                  {free.length > 0 && (
                    <div className="flex gap-1" role="group" aria-label={`Set ${section.label}`}>
                      <button
                        type="button"
                        onClick={() =>
                          onChange(
                            [],
                            free.filter(({ k, s }) => s.checked(k)).map(({ k }) => k),
                          )
                        }
                        className="h-8 rounded-md border border-border bg-white px-2 text-xs text-muted-foreground hover:bg-muted"
                      >
                        None
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(
                            free.filter(({ k, s }) => k === s.view && !s.checked(k)).map(({ k }) => k),
                            free.filter(({ k, s }) => k !== s.view && s.checked(k)).map(({ k }) => k),
                          );
                          setOpenTo(true);
                        }}
                        className="h-8 rounded-md border border-border bg-white px-2 text-xs text-muted-foreground hover:bg-muted"
                      >
                        View only
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(
                            free.filter(({ k, s }) => !s.checked(k)).map(({ k }) => k),
                            [],
                          );
                          setOpenTo(true);
                        }}
                        className="h-8 rounded-md border border-border bg-white px-2 text-xs text-muted-foreground hover:bg-muted"
                      >
                        Full
                      </button>
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div className="border-t border-border">
                    <div className="grid grid-cols-[minmax(0,1fr)_repeat(4,64px)] bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <span>Screen</span>
                      {COLUMNS.map(([, label]) => (
                        <span key={label} className="text-center">
                          {label}
                        </span>
                      ))}
                    </div>
                    {screens.map((screen) => {
                      const groupHeading =
                        screen.group && screen.group !== lastGroup ? (
                          <div className="border-t border-border px-3 pb-1 pt-2.5 text-xs font-bold text-primary">
                            {screen.group}
                          </div>
                        ) : null;
                      lastGroup = screen.group;
                      if (screen.lockedTo) {
                        const status = lockStatus(screen.lockedTo);
                        return (
                          <div key={screen.label}>
                            {groupHeading}
                            <div className="grid grid-cols-[minmax(0,1fr)_256px] items-center border-t border-border/60 px-3 py-1.5">
                              <span className="py-1.5">
                                <span className="block text-sm text-muted-foreground">{screen.label}</span>
                                <span className="block text-xs text-muted-foreground">{screen.hint}</span>
                              </span>
                              <span
                                className={`inline-flex items-center gap-1.5 justify-self-center rounded-full px-3 py-1 text-xs font-semibold ${
                                  status.on ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                <Lock className="h-3 w-3" aria-hidden="true" /> {status.text}
                              </span>
                            </div>
                          </div>
                        );
                      }
                      const { keys: rowKeys, checked } = rowState(screen);
                      const anyOn = rowKeys.some((k) => checked(k));
                      return (
                        <div key={screen.label}>
                          {groupHeading}
                          <div className="grid grid-cols-[minmax(0,1fr)_repeat(4,64px)] items-start border-t border-border/60 px-3 py-1">
                            <div className="flex min-w-0 flex-col gap-0.5 py-1.5">
                              <span
                                className={`text-sm font-medium ${anyOn ? 'text-foreground' : 'text-muted-foreground'}`}
                              >
                                {screen.label}
                              </span>
                              <span className="text-xs text-muted-foreground">{screen.hint}</span>
                              {screen.note && <span className="text-xs text-amber-700">{screen.note}</span>}
                              {screen.more && (
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                  {screen.more.map((m) => pill(screen, m))}
                                </div>
                              )}
                            </div>
                            {COLUMNS.map(([column, label]) => {
                              const action = screen[column];
                              return action ? (
                                <div key={column}>{box(screen, action, label)}</div>
                              ) : (
                                <span
                                  key={column}
                                  title="This screen has no such action"
                                  className="flex min-h-11 items-center justify-center text-muted-foreground/60"
                                >
                                  —
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
